import { SelectFilterItem } from "@/components/filters";
import { getWorkShiftStatusLabel, zWorkShiftStatusSchema, } from "@/types/workShift/workShift.common";
import { onError } from "@/utils/onError";
import { makeAutoObservable } from "mobx";
import { DataTableStore } from "../../stores/DataTableStore";
import { FiltersStore } from "../../stores/FiltersStore";
/**
 * WorkShiftsStore following SOLID principles
 * - Single Responsibility: Only manages work shift-related state
 * - Dependency Inversion: Depends on abstractions (interfaces) not concrete implementations
 * - Open/Closed: Easy to extend with new functionality
 * - Includes integrated filters functionality
 */
export class WorkShiftsStore {
    // Filters functionality
    constructor(workShiftsService, technicianService) {
        this.workShiftsService = workShiftsService;
        this.technicianService = technicianService;
        this.dataTableStore = new DataTableStore({
            dataLoader: {
                loadData: (params) => this.workShiftsService.getShiftsWithParams(params),
            },
        });
        this.filtersStore = new FiltersStore();
        // Loading and error state
        this.isLoading = false;
        // === INITIALIZATION ===
        // Фильтры (список техников) грузятся при первом открытии страницы смен, а не в
        // конструкторе: RootStore создаётся для любого посетителя, включая корп-админа и
        // пользователя без доступа, — запрос к employees на старте им не положен (FR-005).
        this.filtersInitialized = false;
        this.init = async () => {
            this.setIsLoading(true);
            try {
                if (!this.filtersInitialized) {
                    await this.initializeWorkShiftFilters();
                    this.setFiltersInitialized(true);
                }
            }
            catch (error) {
                onError(error, "Failed to load work shifts");
            }
            finally {
                this.setIsLoading(false);
            }
        };
        /**
         * Delete a work shift
         */
        this.deleteWorkShift = async (shiftId) => {
            this.setIsLoading(true);
            try {
                await this.workShiftsService.deleteShift(shiftId);
                await this.dataTableStore.loadData();
            }
            catch (error) {
                onError(error, "Failed to delete work shift");
                throw error;
            }
            finally {
                this.setIsLoading(false);
            }
        };
        /**
         * Copy a work shift to next day
         */
        this.copyWorkShiftNextDay = async (shiftId) => {
            this.setIsLoading(true);
            try {
                const copiedShift = await this.workShiftsService.copyShiftNextDay(shiftId);
                await this.dataTableStore.loadData(); // Reload to get updated list
                return copiedShift;
            }
            catch (error) {
                onError(error, "Failed to copy work shift to next day");
                throw error;
            }
            finally {
                this.setIsLoading(false);
            }
        };
        // === UTILITY METHODS ===
        /**
         * Initialize work shift-specific filter configuration
         */
        this.initializeWorkShiftFilters = async () => {
            const technicians = await this.technicianService.getTechniciansWithParams({
                page: 1,
                pageSize: 1000,
            });
            const filterItems = [
                // Status Filter
                {
                    id: "status-filter",
                    Cmp: SelectFilterItem,
                    props: {
                        name: "status",
                        label: "Статус смены",
                        placeholder: "Выберите статусы",
                        allowClear: true,
                        mode: "multiple",
                        options: Object.values(zWorkShiftStatusSchema.enum).map((status) => ({
                            label: getWorkShiftStatusLabel(status),
                            value: status,
                        })),
                        onChange: (value) => this.filtersStore.updateFilter("status", value),
                    },
                    visible: true,
                    order: 1,
                },
                // Technician Filter
                {
                    id: "technician-filter",
                    Cmp: SelectFilterItem,
                    props: {
                        name: "idTechnicians",
                        label: "Техники",
                        placeholder: "Выберите техников",
                        allowClear: true,
                        mode: "multiple",
                        options: technicians.data.map((technician) => ({
                            label: technician.firstName + " " + technician.lastName,
                            value: technician.id,
                        })),
                        onChange: (value) => this.filtersStore.updateFilter("idTechnicians", value),
                    },
                    visible: true,
                    order: 2,
                },
            ];
            this.filtersStore.setFilterItems(filterItems);
        };
        // === PRIVATE SETTER METHODS ===
        this.setIsLoading = (loading) => {
            this.isLoading = loading;
        };
        this.setFiltersInitialized = (initialized) => {
            this.filtersInitialized = initialized;
        };
        makeAutoObservable(this);
    }
}
